import { useMemo, useState } from 'react';
import type { BrouillonItem, Item } from '@camellie/shared';
import { Confirmation } from '../composants/Confirmation';

interface Props {
  items: Item[];
  onBasculer: (id: string) => void;
  onAjouter: (brouillon: BrouillonItem) => void;
  onSupprimer: (id: string) => void;
}

/**
 * La liste de courses.
 *
 * Trois choix qui la distinguent du reste de l'app :
 *
 * 1. **Aucun filtre par personne.** Une liste d'epicerie qui cache la moitie
 *    des articles ne sert a rien : on est devant le rayon, on veut tout voir.
 *    D'ou l'absence de bascule Moi/Tout ici, alors qu'elle est partout ailleurs.
 *
 * 2. **Ni date ni rappel.** Un article n'a pas d'echeance. Il n'apparait donc
 *    ni dans le brief, ni dans l'agenda — les filtres existants l'excluent
 *    deja naturellement.
 *
 * 3. **Le champ garde le focus apres l'ajout.** On saisit une liste d'un trait,
 *    pas un article a la fois.
 */
export function EcranCourses({ items, onBasculer, onAjouter, onSupprimer }: Props) {
  const [titre, setTitre] = useState('');
  const [vider, setVider] = useState(false);

  const { aPrendre, pris } = useMemo(() => {
    const courses = items.filter((i) => i.type === 'course');
    return {
      // Les plus recemment ajoutes en bas : la liste se lit dans l'ordre ou
      // elle a ete ecrite.
      aPrendre: courses
        .filter((i) => i.statut === 'todo')
        .sort((a, b) => a.cree_le.localeCompare(b.cree_le)),
      pris: courses
        .filter((i) => i.statut === 'fait')
        .sort((a, b) => (b.complete_le ?? '').localeCompare(a.complete_le ?? '')),
    };
  }, [items]);

  function soumettre(e: React.FormEvent) {
    e.preventDefault();
    const propre = titre.trim();
    if (propre === '') return;
    onAjouter({ type: 'course', titre: propre });
    setTitre('');
  }

  function viderPris() {
    for (const i of pris) onSupprimer(i.id);
    setVider(false);
  }

  return (
    <>
      <div className="entete">
        <h1>Courses</h1>
        <span className="sous-titre">
          {aPrendre.length === 0 ? 'Liste vide' : String(aPrendre.length) + ' article' + (aPrendre.length > 1 ? 's' : '')}
        </span>
      </div>

      <div className="contenu">
        <form onSubmit={soumettre}>
          <div className="ajout">
            <input
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Ajouter un article"
              // « done » fermerait le clavier a chaque article ; « next » le
              // laisse ouvert pour enchainer toute la liste.
              enterKeyHint="next"
              autoComplete="off"
              autoCapitalize="sentences"
              aria-label="Nouvel article"
            />
            <button type="submit" className="bouton primaire" disabled={titre.trim() === ''}>
              Ajouter
            </button>
          </div>
        </form>

        {aPrendre.length === 0 && pris.length === 0 && (
          <div className="vide">
            <strong>Rien sur la liste.</strong>
            Ajoute le premier article ci-dessus.
          </div>
        )}

        {aPrendre.length > 0 && (
          <section className="section">
            <div className="liste">
              {aPrendre.map((i) => (
                <LigneCourse key={i.id} item={i} onBasculer={onBasculer} onSupprimer={onSupprimer} />
              ))}
            </div>
          </section>
        )}

        {pris.length > 0 && (
          <section className="section">
            <h2>{pris.length} pris</h2>
            <div className="liste">
              {pris.map((i) => (
                <LigneCourse key={i.id} item={i} onBasculer={onBasculer} onSupprimer={onSupprimer} />
              ))}
            </div>
            <button
              type="button"
              className="bouton pleine-largeur"
              style={{ marginTop: 'var(--r3)' }}
              onClick={() => setVider(true)}
            >
              Vider les articles pris
            </button>
          </section>
        )}
      </div>

      {vider && (
        <Confirmation
          message={'Retirer les ' + pris.length + ' articles deja pris de la liste ?'}
          action="Vider"
          danger
          onConfirmer={viderPris}
          onAnnuler={() => setVider(false)}
        />
      )}
    </>
  );
}

function LigneCourse({
  item,
  onBasculer,
  onSupprimer,
}: {
  item: Item;
  onBasculer: (id: string) => void;
  onSupprimer: (id: string) => void;
}) {
  const pris = item.statut === 'fait';
  const provisoire = item.id.startsWith('tmp_');

  return (
    <div className={'ligne' + (pris ? ' faite' : '') + (provisoire ? ' en-attente' : '')}>
      <button
        type="button"
        className="ligne-action"
        onClick={() => onBasculer(item.id)}
        aria-pressed={pris}
        aria-label={item.titre + (pris ? ', pris' : ', a prendre')}
        disabled={provisoire}
      >
        <span className={'case-zone' + (pris ? ' cochee' : '')} aria-hidden="true">
          <span className="boite">
            <svg viewBox="0 0 24 24">
              <polyline points="4 12.5 9.5 18 20 6.5" />
            </svg>
          </span>
        </span>
        <span className="corps">
          <span className="titre">{item.titre}</span>
        </span>
      </button>

      {!provisoire && (
        <button
          type="button"
          className="supprimer"
          onClick={() => onSupprimer(item.id)}
          aria-label={'Retirer ' + item.titre}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}
